/**
 * Choosing objectives and the KPIs each brings (ObjectiveKpiPicker), and how a KPI reads:
 * its target and how often it is measured.
 */

export interface PickableKpi {
  id: string
  name: string
  description?: string | null
  target_value?: number | null
  unit?: string | null
  frequency?: string | null
  measurement_mode?: string | null
}

export interface PickableObjective {
  id: string
  title: string
  description?: string | null
  kpaName?: string | null
  kpis: PickableKpi[]
  /** Can't be chosen: already where it would go. */
  alreadyAdded?: boolean
  /** How many hold it, when the list gathers several people's objectives. */
  heldBy?: number
}

/** The chosen objectives, by id, each with the ids of its chosen KPIs. */
export type KpiSelection = Record<string, string[]>

const number = (value: number) => String(Number(value.toFixed(2)))

/** What a KPI aims for, as people read it: "Target 90%", "Target 12 days", "Achieved or not",
 *  "Rated on a scale". */
export function kpiTarget(kpi: PickableKpi): string | null {
  if (kpi.measurement_mode === 'binary') return 'Achieved or not'
  if (kpi.measurement_mode === 'rating') return 'Rated on a scale'
  if (kpi.target_value === null || kpi.target_value === undefined) return null
  const unit = (kpi.unit ?? '').trim()
  if (unit === '%' || kpi.measurement_mode === 'percentage') return `Target ${number(kpi.target_value)}%`
  return `Target ${number(kpi.target_value)}${unit ? ` ${unit}` : ''}`
}

const FREQUENCIES: Record<string, string> = {
  weekly: 'weekly',
  monthly: 'monthly',
  quarterly: 'quarterly',
  'bi-annually': 'twice a year',
  'semi-annually': 'twice a year',
  annually: 'yearly',
  yearly: 'yearly',
}

/** How often a KPI is measured, as a phrase: "quarterly", "twice a year". */
export function kpiFrequency(kpi: PickableKpi): string | null {
  const raw = (kpi.frequency ?? '').trim().toLowerCase()
  if (!raw) return null
  return FREQUENCIES[raw] ?? raw.replace(/[-_]/g, ' ')
}

/** The objectives chosen, and how many KPIs they bring between them. */
export function selectionCounts(selection: KpiSelection): { objectives: number; kpis: number } {
  const chosen = Object.values(selection)
  return { objectives: chosen.length, kpis: chosen.reduce((sum, ids) => sum + ids.length, 0) }
}

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** "2 objectives (5 KPIs)"; "1 objective" when none has a KPI. */
export function describeSelection(selection: KpiSelection): string {
  const { objectives, kpis } = selectionCounts(selection)
  return kpis ? `${plural(objectives, 'objective')} (${plural(kpis, 'KPI')})` : plural(objectives, 'objective')
}

/** The KPIs to name in a copy (POST /objectives/copy): only for objectives bringing fewer than
 *  all of theirs; an objective not named brings all. */
export function kpiChoice(selection: KpiSelection, offered: PickableObjective[]): Record<string, string[]> | undefined {
  const all = new Map(offered.map(o => [o.id, o.kpis.length]))
  const narrowed = Object.entries(selection).filter(([id, kpis]) => kpis.length < (all.get(id) ?? 0))
  return narrowed.length ? Object.fromEntries(narrowed) : undefined
}
